import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { getSupabaseClient } from '@/lib/supabase';

export default function SignInPage() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const { signIn, signInWithOAuth, user } = useAuth();

  const supabaseReady = useMemo(() => !!getSupabaseClient(), []);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showVerifiedMessage, setShowVerifiedMessage] = useState(false);

  // 이메일 확인 후 자동 세션 처리되면 메시지 표시
  useEffect(() => {
    if (user) {
      setShowVerifiedMessage(true);
    }
  }, [user]);

  const handleSignOut = async () => {
    const supabase = getSupabaseClient();
    if (supabase) {
      await supabase.auth.signOut();
    }
    setShowVerifiedMessage(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!supabaseReady) {
      setError(t('signIn.error.noSupabase') || 'Server connection is not configured. Please contact support.');
      return;
    }

    setLoading(true);

    try {
      await signIn(email, password);
      navigate('/');
    } catch (err: any) {
      const msg = err?.message || '';
      if (msg.includes('Supabase not configured') || msg.includes('not connected')) {
        setError(t('signIn.error.noSupabase') || 'Server connection is not configured. Please contact support.');
      } else if (msg.includes('Invalid login credentials')) {
        setError(t('signIn.error.invalid'));
      } else {
        setError(msg || t('signIn.error.generic'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOAuth = async (provider: 'google' | 'facebook') => {
    setError('');
    if (!supabaseReady) {
      setError(t('signIn.error.noSupabase') || 'Server connection is not configured. Please contact support.');
      return;
    }
    try {
      await signInWithOAuth(provider);
    } catch (err: any) {
      const msg = err?.message || '';
      if (msg.includes('Supabase not configured') || msg.includes('not connected')) {
        setError(t('signIn.error.noSupabase') || 'Server connection is not configured. Please contact support.');
      } else {
        setError(msg || t('signIn.error.generic'));
      }
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-xl border border-stone-200 p-8 shadow-sm">
          {/* 이메일 확인 완료 메시지 */}
          {showVerifiedMessage && user && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg text-center">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
                <i className="ri-mail-check-line text-xl text-green-600" />
              </div>
              <h3 className="text-base font-semibold text-green-800 mb-1">
                이메일 확인이 완료되었습니다
              </h3>
              <p className="text-sm text-green-700 mb-3">
                이제 로그인하여 서비스를 이용하실 수 있습니다.
              </p>
              <div className="flex flex-col gap-2">
                <button
                  onClick={() => navigate('/', { replace: true })}
                  className="w-full py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold rounded-lg transition-colors whitespace-nowrap"
                >
                  홈으로 이동
                </button>
                <button
                  onClick={handleSignOut}
                  className="w-full py-2 bg-white border border-stone-300 hover:bg-stone-50 text-stone-600 text-sm font-medium rounded-lg transition-colors whitespace-nowrap"
                >
                  다른 계정으로 로그인
                </button>
              </div>
            </div>
          )}

          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-12 h-12 bg-amber-600 rounded-xl flex items-center justify-center mx-auto mb-4">
              <i className="ri-box-3-line text-white text-2xl" />
            </div>
            <h1 className="text-2xl font-bold text-stone-900">{t('signIn.title')}</h1>
            <p className="text-sm text-stone-500 mt-1">{t('signIn.subtitle')}</p>
          </div>

          {/* Error */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center gap-2">
              <i className="ri-error-warning-line" />
              {error}
            </div>
          )}

          {/* Form - 로그인된 상태에서는 숨김 */}
          {!user && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1.5">
                  {t('signIn.email')}
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-3.5 py-2.5 text-sm border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-colors"
                  placeholder="you@company.com"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-sm font-medium text-stone-700">
                    {t('signIn.password')}
                  </label>
                  <a href="/forgot-password" className="text-xs text-amber-600 hover:text-amber-700 font-medium">
                    {t('signIn.forgotPassword')}
                  </a>
                </div>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full px-3.5 py-2.5 text-sm border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-colors"
                  placeholder="••••••••"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-2.5 bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin" />
                    Loading...
                  </span>
                ) : (
                  t('signIn.button')
                )}
              </button>
            </form>
          )}

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-stone-200" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-3 bg-white text-stone-400">{t('signIn.or')}</span>
            </div>
          </div>

          {/* OAuth - 로그인된 상태에서는 숨김 */}
          {!user && (
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => handleOAuth('google')}
                className="flex items-center justify-center gap-2 px-4 py-2.5 border border-stone-300 rounded-lg text-sm font-medium text-stone-700 hover:bg-stone-50 transition-colors whitespace-nowrap"
              >
                <i className="ri-google-fill text-lg text-red-500" />
                {t('signIn.google')}
              </button>
              <button
                onClick={() => handleOAuth('facebook')}
                className="flex items-center justify-center gap-2 px-4 py-2.5 border border-stone-300 rounded-lg text-sm font-medium text-stone-700 hover:bg-stone-50 transition-colors whitespace-nowrap"
              >
                <i className="ri-facebook-fill text-lg text-blue-600" />
                {t('signIn.facebook')}
              </button>
            </div>
          )}

          {/* Sign up link - 로그인된 상태에서는 숨김 */}
          {!user && (
            <p className="text-center text-sm text-stone-500 mt-6">
              {t('signIn.noAccount')}{' '}
              <a href="/sign-up" className="text-amber-600 hover:text-amber-700 font-semibold">
                {t('signIn.signUpLink')}
              </a>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}